import { DisplayMarketAsset, getPointsLabel, PointsItem } from "@silo/types";
import { ReactNode } from "react";
import { cn } from "../../utils/class-names";
import { Perk } from "../perk";
import { SuperfestIcon } from "../../icons";
import { PointsIcon } from "../perk/points-icon";
import { getDisplayCollateralYields } from "@silo/utils";

interface PerksAsset {
  symbol: string;
  hasPerks: boolean;
  hasDepositPerks: boolean;
  hasDebtPerks: boolean;
  hasDepositSuperFest: boolean;
  hasDebtSuperFest: boolean;
  depositPoints: readonly PointsItem[];
  debtPoints: readonly PointsItem[];
  hasDepositPoints: boolean;
  hasDebtPoints: boolean;
}

export const CardPerks = {
  Root,
  Asset,
};

interface RootProps {
  className?: string;
  children?: ReactNode;
}

function Root({ className, children }: RootProps): JSX.Element {
  return <div className={cn("flex flex-col gap-5", className)}>{children}</div>;
}

interface AssetProps {
  asset: PerksAsset;
}

function Asset({ asset }: AssetProps): JSX.Element {
  const {
    symbol,
    hasDepositPerks,
    hasDepositSuperFest,
    hasDepositPoints,
    depositPoints,
    hasDebtPerks,
    hasDebtSuperFest,
    hasDebtPoints,
    debtPoints,
  } = asset;

  return (
    <>
      {hasDepositPerks && (
        <Perk.Root>
          <Perk.Label>{symbol} depositors get:</Perk.Label>

          {hasDepositSuperFest && <SuperfestRewards />}

          {hasDepositPoints &&
            depositPoints.map((pointsItem) => (
              <PointsPerk pointsItem={pointsItem} />
            ))}
        </Perk.Root>
      )}

      {hasDebtPerks && (
        <Perk.Root>
          <Perk.Label>{symbol} borrowers get:</Perk.Label>

          {hasDebtSuperFest && <SuperfestRewards />}

          {hasDebtPoints &&
            debtPoints.map((pointsItem) => (
              <PointsPerk pointsItem={pointsItem} />
            ))}
        </Perk.Root>
      )}
    </>
  );
}

interface SuperfestRewardsProps {
  className?: string;
}

function SuperfestRewards({ className }: SuperfestRewardsProps): JSX.Element {
  return (
    <Perk.Item className={className}>
      <SuperfestIcon className={Perk.iconClassName} />

      <Perk.Emphasis>SuperFest</Perk.Emphasis>

      <Perk.RewardsLabel />
    </Perk.Item>
  );
}

interface PointsPerkProps {
  className?: string;
  pointsItem: PointsItem;
}

function PointsPerk({ className, pointsItem }: PointsPerkProps): JSX.Element {
  const pointsKey = pointsItem._tag;
  const label = getPointsLabel(pointsItem);

  return (
    <Perk.Item className={className}>
      <PointsIcon className={Perk.iconClassName} pointsKey={pointsKey} />

      <Perk.Emphasis>{label}</Perk.Emphasis>

      <Perk.PointsLabel />
    </Perk.Item>
  );
}

export function mapToPerksAsset(asset: DisplayMarketAsset): PerksAsset {
  const {
    symbol,
    depositMode,
    collateralMerklRewards,
    protectedMerklRewards,
    debtMerklRewards,
    collateralPoints,
    protectedPoints,
    debtPoints,
  } = asset;

  const displayCollateralYields = getDisplayCollateralYields(depositMode);

  const depositMerklRewards = displayCollateralYields
    ? collateralMerklRewards
    : protectedMerklRewards;

  const hasDepositSuperFest = depositMerklRewards.some(
    (reward) => reward.isSuperFest
  );
  const hasDebtSuperFest = debtMerklRewards.some(
    (reward) => reward.isSuperFest
  );

  const depositPoints = displayCollateralYields
    ? collateralPoints
    : protectedPoints;

  const hasDepositPoints = depositPoints.length > 0;
  const hasDebtPoints = debtPoints.length > 0;

  const hasDepositPerks = hasDepositSuperFest || hasDepositPoints;
  const hasDebtPerks = hasDebtSuperFest || hasDebtPoints;

  const hasPerks = hasDepositPerks || hasDebtPerks;

  const perksAsset: PerksAsset = {
    symbol,
    hasPerks,
    hasDepositPerks,
    hasDebtPerks,
    hasDepositSuperFest,
    hasDebtSuperFest,
    depositPoints,
    debtPoints,
    hasDepositPoints,
    hasDebtPoints,
  };

  return perksAsset;
}
